import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { LOJ_COLORS } from '../theme/lojTheme';
import { useTranslation } from '../i18n/LanguageContext';

const SiteHeader: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box
      component="header"
      sx={{
        position: 'relative',
        textAlign: 'center',
        pt: { xs: 5, md: 7 },
        pb: { xs: 3, md: 4 },
        px: 2,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(ellipse 80% 60% at 50% -10%,
              ${alpha(LOJ_COLORS.goldBright, 0.12)} 0%,
              transparent 70%
            )
          `,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Top accent line */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(to right, transparent 0%, ${LOJ_COLORS.goldDim} 20%, ${LOJ_COLORS.goldGlow} 50%, ${LOJ_COLORS.goldDim} 80%, transparent 100%)`,
          boxShadow: `0 0 12px ${alpha(LOJ_COLORS.goldBright, 0.6)}`,
        }}
      />

      {/* Alliance badge row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ color: LOJ_COLORS.goldGlow, fontSize: '1.2rem', opacity: 0.7 }}>⚜</Box>
        <Typography
          sx={{
            fontFamily: "'Cinzel', serif",
            fontSize: { xs: '0.65rem', md: '0.75rem' },
            letterSpacing: '0.4em',
            color: LOJ_COLORS.goldMuted,
            textTransform: 'uppercase',
          }}
        >
          {t('Lands of Jail · Alliance War Council')}
        </Typography>
        <Box sx={{ color: LOJ_COLORS.goldGlow, fontSize: '1.2rem', opacity: 0.7 }}>⚜</Box>
      </Box>

      {/* Main title */}
      <Typography
        variant="h1"
        sx={{
          fontSize: { xs: '2rem', sm: '2.8rem', md: '3.8rem', lg: '4.5rem' },
          lineHeight: 1.1,
          mb: 1,
          background: `linear-gradient(180deg, ${LOJ_COLORS.goldGlow} 0%, ${LOJ_COLORS.goldBright} 50%, ${LOJ_COLORS.goldMuted} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textShadow: 'none',
          filter: `drop-shadow(0 0 20px ${alpha(LOJ_COLORS.goldGlow, 0.4)})`,
        }}
      >
        UnitedWeStand
      </Typography>

      {/* EWU tag */}
      <Box
        sx={{
          display: 'inline-block',
          border: `1px solid ${alpha(LOJ_COLORS.goldBright, 0.5)}`,
          px: 2.5,
          py: 0.5,
          mb: 2,
          background: alpha(LOJ_COLORS.goldDim, 0.15),
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Cinzel', serif",
            fontSize: { xs: '0.75rem', md: '0.9rem' },
            letterSpacing: '0.5em',
            color: LOJ_COLORS.goldBright,
            fontWeight: 600,
          }}
        >
          [ EWU ]
        </Typography>
      </Box>

      {/* Subtitle */}
      <Typography
        variant="body2"
        sx={{
          maxWidth: 520,
          mx: 'auto',
          color: LOJ_COLORS.parchmentDark,
          fontSize: '1rem',
          fontStyle: 'italic',
          letterSpacing: '0.03em',
        }}
      >
        "{t('Forged in battle. Bound by honor. United we conquer the Lands of Jail.')}"
      </Typography>

      {/* Bottom ornamental divider */}
      <Box
        sx={{
          mt: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0,
          px: { xs: 2, md: 8 },
        }}
      >
        <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(to right, transparent, ${alpha(LOJ_COLORS.goldBright, 0.6)})` }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            color: LOJ_COLORS.goldBright,
            fontSize: '0.9rem',
          }}
        >
          <span>◆</span>
          <span style={{ fontSize: '1.2rem' }}>✦</span>
          <span>◆</span>
        </Box>
        <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(to left, transparent, ${alpha(LOJ_COLORS.goldBright, 0.6)})` }} />
      </Box>
    </Box>
  );
};

export default SiteHeader;
